export const MAX_CHARACTER_JOINTS = 16;

export type CharacterClipName = "IDLE" | "WALK" | "RUN" | "SWIM" | "SPRINT" | "CROUCH_IDLE" | "CROUCH_WALK" | "WALK_BACK" | "STRAFE_LEFT" | "STRAFE_RIGHT" | "TURN_LEFT" | "TURN_RIGHT" | "GET_UP_FRONT" | "GET_UP_BACK";
export type CharacterTrackPath = "translation" | "rotation" | "scale";

export interface CharacterGeometry {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly colors: Float32Array;
  readonly joints: Uint8Array;
  readonly weights: Float32Array;
  readonly indices: Uint16Array;
}

export interface CharacterJoint {
  readonly name: string;
  readonly parent: number;
  readonly translation: readonly [number, number, number];
  readonly rotation: readonly [number, number, number, number];
  readonly scale: readonly [number, number, number];
}

export interface CharacterAnimationTrack {
  readonly joint: number;
  readonly path: CharacterTrackPath;
  readonly times: Float32Array;
  readonly values: Float32Array;
}

export interface CharacterAnimationClip {
  readonly name: CharacterClipName;
  readonly duration: number;
  readonly tracks: readonly CharacterAnimationTrack[];
}

export interface CharacterVisualGroup { readonly id: string; readonly firstIndex: number; readonly indexCount: number }

export interface CharacterAsset {
  readonly visualGroups?: readonly CharacterVisualGroup[];
  readonly id: string;
  readonly geometry: CharacterGeometry;
  readonly baseColor: readonly [number, number, number, number];
  readonly joints: readonly CharacterJoint[];
  readonly inverseBindMatrices: Float32Array;
  readonly clips: Readonly<Record<"IDLE" | "WALK" | "RUN", CharacterAnimationClip> & Partial<Record<CharacterClipName, CharacterAnimationClip>>>;
  readonly standingHeight: number;
  readonly groundOriginY: number;
  readonly boundsCenterY: number;
  readonly boundsRadius: number;
  readonly sourceBytes: number;
  readonly meshBufferBytes: number;
  readonly animationBytes: number;
}

interface GltfBufferView {
  readonly buffer?: number;
  readonly byteOffset?: number;
  readonly byteLength: number;
  readonly byteStride?: number;
}

interface GltfAccessor {
  readonly bufferView: number;
  readonly byteOffset?: number;
  readonly componentType: number;
  readonly normalized?: boolean;
  readonly count: number;
  readonly type: string;
  readonly sparse?: unknown;
}

interface GltfNode {
  readonly name?: string;
  readonly mesh?: number;
  readonly skin?: number;
  readonly children?: readonly number[];
  readonly translation?: readonly number[];
  readonly rotation?: readonly number[];
  readonly scale?: readonly number[];
  readonly matrix?: readonly number[];
  readonly extras?: {
    readonly stableAssetId?: string;
    readonly visualGroups?: readonly CharacterVisualGroup[];
    readonly standingHeight?: number;
    readonly groundOriginY?: number;
    readonly boundsCenterY?: number;
    readonly boundsRadius?: number;
  };
}

interface GltfDocument {
  readonly asset?: { readonly version?: string };
  readonly nodes?: readonly GltfNode[];
  readonly meshes?: readonly {
    readonly primitives?: readonly {
      readonly attributes?: Readonly<Record<string, number>>;
      readonly indices?: number;
      readonly material?: number;
      readonly mode?: number;
    }[];
  }[];
  readonly skins?: readonly {
    readonly inverseBindMatrices?: number;
    readonly joints?: readonly number[];
    readonly skeleton?: number;
  }[];
  readonly animations?: readonly {
    readonly name?: string;
    readonly samplers?: readonly {
      readonly input?: number;
      readonly output?: number;
      readonly interpolation?: string;
    }[];
    readonly channels?: readonly {
      readonly sampler?: number;
      readonly target?: { readonly node?: number; readonly path?: string };
    }[];
  }[];
  readonly materials?: readonly {
    readonly pbrMetallicRoughness?: { readonly baseColorFactor?: readonly number[] };
  }[];
  readonly accessors?: readonly GltfAccessor[];
  readonly bufferViews?: readonly GltfBufferView[];
  readonly buffers?: readonly { readonly byteLength?: number }[];
}

const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK = 0x4e4f534a;
const BINARY_CHUNK = 0x004e4942;
const COMPONENT_BYTES: Readonly<Record<number, number>> = Object.freeze({
  5120: 1,
  5121: 1,
  5122: 2,
  5123: 2,
  5125: 4,
  5126: 4,
});
const TYPE_COMPONENTS: Readonly<Record<string, number>> = Object.freeze({
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT4: 16,
});

export async function loadCharacterGlb(url: string): Promise<CharacterAsset> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Character asset request failed: ${url}`);
  return parseCharacterGlb(await response.arrayBuffer());
}

/** Focused glTF 2.0 reader: one skinned mesh, <=16 joints and LINEAR TRS clips. */
export function parseCharacterGlb(data: ArrayBuffer): CharacterAsset {
  const { document, binary } = readGlb(data);
  if (document.buffers?.length !== 1) throw new Error("ENDCLOCK characters require one embedded buffer");
  const nodes = document.nodes;
  if (!nodes) throw new Error("Character asset has no nodes");
  const meshNodes = nodes.map((node, index) => ({ node, index })).filter(({ node }) => node.mesh !== undefined);
  if (meshNodes.length !== 1) throw new Error("ENDCLOCK characters require one mesh node");
  const { node: meshNode } = meshNodes[0]!;
  if (meshNode.matrix || hasNonIdentityTransform(meshNode)) {
    throw new Error("Character mesh node must use an identity transform");
  }
  const skin = meshNode.skin === undefined ? undefined : document.skins?.[meshNode.skin];
  const jointNodes = skin?.joints;
  if (!skin || !jointNodes || jointNodes.length < 1 || jointNodes.length > MAX_CHARACTER_JOINTS) {
    throw new Error(`Character must use one skin with 1-${MAX_CHARACTER_JOINTS} joints`);
  }
  if (skin.inverseBindMatrices === undefined) throw new Error("Character skin requires inverse bind matrices");

  const mesh = meshNode.mesh === undefined ? undefined : document.meshes?.[meshNode.mesh];
  const primitives = mesh?.primitives;
  if (!primitives || primitives.length !== 1) throw new Error("ENDCLOCK characters require one mesh primitive");
  const primitive = primitives[0]!;
  if ((primitive.mode ?? 4) !== 4 || primitive.indices === undefined) {
    throw new Error("Character primitive must use indexed triangles");
  }
  const attributes = primitive.attributes;
  const positionAccessor = attributes?.POSITION;
  const normalAccessor = attributes?.NORMAL;
  const colorAccessor = attributes?.COLOR_0;
  const jointAccessor = attributes?.JOINTS_0;
  const weightAccessor = attributes?.WEIGHTS_0;
  if (
    positionAccessor === undefined ||
    normalAccessor === undefined ||
    colorAccessor === undefined ||
    jointAccessor === undefined ||
    weightAccessor === undefined
  ) {
    throw new Error("Character requires POSITION, NORMAL, COLOR_0, JOINTS_0 and WEIGHTS_0");
  }
  const positions = readFloatAccessor(document, binary, positionAccessor, "VEC3");
  const normals = readFloatAccessor(document, binary, normalAccessor, "VEC3");
  const colors = readColors(document, binary, colorAccessor);
  const joints = readJoints(document, binary, jointAccessor, jointNodes.length);
  const weights = readWeights(document, binary, weightAccessor);
  const indices = readIndices(document, binary, primitive.indices);
  const vertexCount = positions.length / 3;
  if (
    normals.length / 3 !== vertexCount ||
    colors.length / 4 !== vertexCount ||
    joints.length / 4 !== vertexCount ||
    weights.length / 4 !== vertexCount ||
    indices.length % 3 !== 0
  ) {
    throw new Error("Character vertex stream lengths do not match");
  }
  normalizeWeights(weights);

  const parentNodes = createParentMap(nodes);
  const nodeToJoint = new Map<number, number>();
  jointNodes.forEach((nodeIndex, jointIndex) => nodeToJoint.set(nodeIndex, jointIndex));
  const jointsDefinition: CharacterJoint[] = jointNodes.map((nodeIndex) => {
    const node = nodes[nodeIndex];
    if (!node || node.matrix) throw new Error("Character joints require TRS nodes");
    const parentNode = parentNodes[nodeIndex] ?? -1;
    const parent = parentNode < 0 ? -1 : nodeToJoint.get(parentNode);
    if (parentNode >= 0 && parent === undefined) throw new Error("Joint hierarchy may only contain skin joints");
    return {
      name: node.name ?? `joint_${nodeIndex}`,
      parent: parent ?? -1,
      translation: vec3(node.translation, [0, 0, 0]),
      rotation: quat(node.rotation),
      scale: vec3(node.scale, [1, 1, 1]),
    };
  });
  validateJointOrder(jointsDefinition);
  const inverseBindMatrices = readFloatAccessor(document, binary, skin.inverseBindMatrices, "MAT4");
  if (inverseBindMatrices.length !== jointNodes.length * 16) throw new Error("Inverse bind matrix count mismatch");

  const clipMap = new Map<CharacterClipName, CharacterAnimationClip>();
  for (const sourceClip of document.animations ?? []) {
    const name = normalizeClipName(sourceClip.name);
    if (!name) continue;
    if (clipMap.has(name)) throw new Error(`Duplicate character animation ${name}`);
    const tracks: CharacterAnimationTrack[] = [];
    let duration = 0;
    for (const channel of sourceClip.channels ?? []) {
      const sampler = channel.sampler === undefined ? undefined : sourceClip.samplers?.[channel.sampler];
      const nodeIndex = channel.target?.node;
      const joint = nodeIndex === undefined ? undefined : nodeToJoint.get(nodeIndex);
      const path = parseTrackPath(channel.target?.path);
      if (!sampler || sampler.input === undefined || sampler.output === undefined || joint === undefined || !path) {
        throw new Error(`Invalid ${name} animation channel`);
      }
      if ((sampler.interpolation ?? "LINEAR") !== "LINEAR") {
        throw new Error("Character animations support LINEAR interpolation only");
      }
      const times = readFloatAccessor(document, binary, sampler.input, "SCALAR");
      const values = readFloatAccessor(document, binary, sampler.output, path === "rotation" ? "VEC4" : "VEC3");
      const width = path === "rotation" ? 4 : 3;
      if (times.length < 1 || values.length !== times.length * width) throw new Error(`Invalid ${name} animation samples`);
      validateTimes(times);
      duration = Math.max(duration, times[times.length - 1]!);
      tracks.push({ joint, path, times, values });
    }
    if (tracks.length === 0 || duration <= 0) throw new Error(`Animation ${name} has no usable tracks`);
    clipMap.set(name, { name, duration, tracks });
  }
  const idle = clipMap.get("IDLE");
  const walk = clipMap.get("WALK");
  const run = clipMap.get("RUN");
  if (!idle || !walk || !run) throw new Error("Character asset requires IDLE, WALK and RUN clips");

  let minimumY = Number.POSITIVE_INFINITY;
  let maximumY = Number.NEGATIVE_INFINITY;
  for (let index = 1; index < positions.length; index += 3) {
    minimumY = Math.min(minimumY, positions[index]!);
    maximumY = Math.max(maximumY, positions[index]!);
  }
  const extras = meshNode.extras;
  const standingHeight = finite(extras?.standingHeight, maximumY - minimumY);
  const groundOriginY = finite(extras?.groundOriginY, minimumY);
  const boundsCenterY = finite(extras?.boundsCenterY, (maximumY + minimumY) * 0.5);
  const boundsRadius = finite(extras?.boundsRadius, standingHeight * 0.56);
  if (standingHeight < 1.7 || standingHeight > 1.85 || Math.abs(groundOriginY) > 0.015 || boundsRadius <= 0) {
    throw new Error("Character scale/origin metadata is outside ENDCLOCK constraints");
  }
  const factor = document.materials?.[primitive.material ?? 0]?.pbrMetallicRoughness?.baseColorFactor;
  const baseColor: [number, number, number, number] = [
    unit(factor?.[0], 1),
    unit(factor?.[1], 1),
    unit(factor?.[2], 1),
    unit(factor?.[3], 1),
  ];
  const visualGroups = extras?.visualGroups;
  if (visualGroups !== undefined) {
    if (!Array.isArray(visualGroups) || visualGroups.length < 1 || visualGroups.length > 16) throw new Error("Invalid character visual groups");
    let end = 0;
    const ids = new Set<string>();
    for (const group of visualGroups) {
      if (!group || typeof group.id !== "string" || !/^[a-z][a-z0-9_]{0,47}$/.test(group.id) || ids.has(group.id)
        || group.firstIndex !== end || !Number.isInteger(group.indexCount) || group.indexCount < 3 || group.indexCount % 3 !== 0)
        throw new Error("Invalid character visual group range");
      ids.add(group.id); end += group.indexCount;
    }
    if (!ids.has("body") || end !== indices.length) throw new Error("Character visual groups must cover the primitive exactly");
  }
  const geometry: CharacterGeometry = { positions, normals, colors, joints, weights, indices };
  const animationBytes = [...clipMap.values()].reduce(
    (sum, clip) => sum + clip.tracks.reduce((trackSum, track) => trackSum + track.times.byteLength + track.values.byteLength, 0),
    0,
  );
  return {
    id: extras?.stableAssetId ?? "survivor_01",
    ...(visualGroups ? { visualGroups } : {}),
    geometry,
    baseColor,
    joints: jointsDefinition,
    inverseBindMatrices,
    clips: { ...Object.fromEntries(clipMap), IDLE: idle, WALK: walk, RUN: run },
    standingHeight,
    groundOriginY,
    boundsCenterY,
    boundsRadius,
    sourceBytes: data.byteLength,
    meshBufferBytes:
      positions.byteLength +
      normals.byteLength +
      colors.byteLength +
      joints.byteLength +
      weights.byteLength +
      indices.byteLength,
    animationBytes,
  };
}

function readGlb(data: ArrayBuffer): { document: GltfDocument; binary: ArrayBuffer } {
  const view = new DataView(data);
  if (view.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new Error("Invalid GLB header");
  if (view.getUint32(4, true) !== 2) throw new Error("Only glTF 2.0 is supported");
  if (view.getUint32(8, true) !== view.byteLength) throw new Error("GLB length mismatch");
  let document: GltfDocument | undefined;
  let binary: ArrayBuffer | undefined;
  for (let offset = 12; offset + 8 <= view.byteLength; ) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const start = offset + 8;
    const end = start + length;
    if (end > view.byteLength) throw new Error("Invalid GLB chunk length");
    if (type === JSON_CHUNK) {
      document = JSON.parse(new TextDecoder().decode(new Uint8Array(data, start, length)).trim()) as GltfDocument;
    } else if (type === BINARY_CHUNK) {
      binary = data.slice(start, end);
    }
    offset = end;
  }
  if (!document || !binary || document.asset?.version !== "2.0") throw new Error("Incomplete glTF 2.0 character");
  return { document, binary };
}

function readFloatAccessor(
  document: GltfDocument,
  binary: ArrayBuffer,
  accessorIndex: number,
  expectedType: string,
): Float32Array {
  const accessor = getAccessor(document, accessorIndex, expectedType);
  if (accessor.componentType !== 5126) throw new Error(`${expectedType} character data must use FLOAT`);
  return readAccessor(document, binary, accessor, (view, offset) => view.getFloat32(offset, true));
}

function readColors(document: GltfDocument, binary: ArrayBuffer, accessorIndex: number): Float32Array {
  const accessor = getAccessor(document, accessorIndex);
  if (accessor.type !== "VEC3" && accessor.type !== "VEC4") throw new Error("COLOR_0 must use VEC3 or VEC4");
  const source = readAccessor(document, binary, accessor, componentReader(accessor.componentType, accessor.normalized === true));
  const width = accessor.type === "VEC4" ? 4 : 3;
  const result = new Float32Array(accessor.count * 4);
  for (let index = 0; index < accessor.count; index += 1) {
    result[index * 4] = source[index * width]!;
    result[index * 4 + 1] = source[index * width + 1]!;
    result[index * 4 + 2] = source[index * width + 2]!;
    result[index * 4 + 3] = width === 4 ? source[index * width + 3]! : 1;
  }
  return result;
}

function readJoints(document: GltfDocument, binary: ArrayBuffer, accessorIndex: number, jointCount: number): Uint8Array {
  const accessor = getAccessor(document, accessorIndex, "VEC4");
  if (accessor.componentType !== 5121 && accessor.componentType !== 5123) {
    throw new Error("JOINTS_0 must use UNSIGNED_BYTE or UNSIGNED_SHORT");
  }
  const source = readAccessor(document, binary, accessor, componentReader(accessor.componentType, false));
  const result = new Uint8Array(source.length);
  for (let index = 0; index < source.length; index += 1) {
    const value = source[index]!;
    if (!Number.isInteger(value) || value < 0 || value >= jointCount) throw new Error("JOINTS_0 references an invalid joint");
    result[index] = value;
  }
  return result;
}

function readWeights(document: GltfDocument, binary: ArrayBuffer, accessorIndex: number): Float32Array {
  const accessor = getAccessor(document, accessorIndex, "VEC4");
  if (accessor.componentType !== 5126 && accessor.componentType !== 5121 && accessor.componentType !== 5123) {
    throw new Error("Unsupported WEIGHTS_0 component type");
  }
  return readAccessor(document, binary, accessor, componentReader(accessor.componentType, accessor.componentType !== 5126));
}

function readIndices(document: GltfDocument, binary: ArrayBuffer, accessorIndex: number): Uint16Array {
  const accessor = getAccessor(document, accessorIndex, "SCALAR");
  if (![5121, 5123, 5125].includes(accessor.componentType)) throw new Error("Unsupported character index type");
  const source = readAccessor(document, binary, accessor, componentReader(accessor.componentType, false));
  const result = new Uint16Array(source.length);
  for (let index = 0; index < source.length; index += 1) {
    const value = source[index]!;
    if (!Number.isInteger(value) || value < 0 || value > 65_535) throw new Error("Character exceeds 16-bit index budget");
    result[index] = value;
  }
  return result;
}

function readAccessor(
  document: GltfDocument,
  binary: ArrayBuffer,
  accessor: GltfAccessor,
  read: (view: DataView, offset: number) => number,
): Float32Array {
  if (accessor.sparse) throw new Error("Sparse character accessors are not supported");
  const components = TYPE_COMPONENTS[accessor.type];
  const componentBytes = COMPONENT_BYTES[accessor.componentType];
  const bufferView = document.bufferViews?.[accessor.bufferView];
  if (!components || !componentBytes || !bufferView || (bufferView.buffer ?? 0) !== 0) {
    throw new Error("Invalid character accessor");
  }
  const packedWidth = components * componentBytes;
  const stride = bufferView.byteStride ?? packedWidth;
  if (stride < packedWidth) throw new Error("Invalid character accessor stride");
  const start = (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  validateRange(binary, bufferView, start, accessor.count, stride, packedWidth);
  const result = new Float32Array(accessor.count * components);
  const view = new DataView(binary);
  for (let index = 0; index < accessor.count; index += 1) {
    const offset = start + index * stride;
    for (let component = 0; component < components; component += 1) {
      result[index * components + component] = read(view, offset + component * componentBytes);
    }
  }
  return result;
}

function componentReader(componentType: number, normalized: boolean): (view: DataView, offset: number) => number {
  if (componentType === 5120) return (view, offset) => normalizeInteger(view.getInt8(offset), 127, normalized);
  if (componentType === 5121) return (view, offset) => normalizeInteger(view.getUint8(offset), 255, normalized);
  if (componentType === 5122) return (view, offset) => normalizeInteger(view.getInt16(offset, true), 32_767, normalized);
  if (componentType === 5123) return (view, offset) => normalizeInteger(view.getUint16(offset, true), 65_535, normalized);
  if (componentType === 5125) return (view, offset) => normalizeInteger(view.getUint32(offset, true), 4_294_967_295, normalized);
  if (componentType === 5126) return (view, offset) => view.getFloat32(offset, true);
  throw new Error("Unsupported character accessor component type");
}

function normalizeInteger(value: number, maximum: number, normalized: boolean): number {
  return normalized ? Math.max(-1, value / maximum) : value;
}

function getAccessor(document: GltfDocument, index: number, expectedType?: string): GltfAccessor {
  const accessor = document.accessors?.[index];
  if (!accessor || (expectedType !== undefined && accessor.type !== expectedType)) {
    throw new Error(`Missing ${expectedType ?? "character"} accessor`);
  }
  return accessor;
}

function validateRange(
  binary: ArrayBuffer,
  bufferView: GltfBufferView,
  start: number,
  count: number,
  stride: number,
  width: number,
): void {
  const viewStart = bufferView.byteOffset ?? 0;
  const requiredEnd = start + Math.max(0, count - 1) * stride + width;
  if (start < viewStart || requiredEnd > viewStart + bufferView.byteLength || requiredEnd > binary.byteLength) {
    throw new Error("Character accessor is outside its buffer view");
  }
}

function createParentMap(nodes: readonly GltfNode[]): Int16Array {
  const parents = new Int16Array(nodes.length);
  parents.fill(-1);
  nodes.forEach((node, parent) => {
    for (const child of node.children ?? []) {
      if (!nodes[child] || parents[child] !== -1) throw new Error("Invalid character node hierarchy");
      parents[child] = parent;
    }
  });
  return parents;
}

function validateJointOrder(joints: readonly CharacterJoint[]): void {
  joints.forEach((joint, index) => {
    if (joint.parent >= index) throw new Error("Character skin joints must be ordered parent-first");
  });
}

function normalizeWeights(weights: Float32Array): void {
  for (let offset = 0; offset < weights.length; offset += 4) {
    const sum = weights[offset]! + weights[offset + 1]! + weights[offset + 2]! + weights[offset + 3]!;
    if (!Number.isFinite(sum) || sum <= 0.0001) throw new Error("Character vertex has no skin weight");
    const inverse = 1 / sum;
    weights[offset] = weights[offset]! * inverse;
    weights[offset + 1] = weights[offset + 1]! * inverse;
    weights[offset + 2] = weights[offset + 2]! * inverse;
    weights[offset + 3] = weights[offset + 3]! * inverse;
  }
}

function validateTimes(times: Float32Array): void {
  let previous = -Infinity;
  for (const value of times) {
    if (!Number.isFinite(value) || value < 0 || value <= previous) throw new Error("Animation times must increase");
    previous = value;
  }
}

function normalizeClipName(name: string | undefined): CharacterClipName | undefined {
  const normalized = name?.trim().toUpperCase();
  return ["IDLE", "WALK", "RUN", "SWIM", "SPRINT", "CROUCH_IDLE", "CROUCH_WALK", "WALK_BACK", "STRAFE_LEFT", "STRAFE_RIGHT", "TURN_LEFT", "TURN_RIGHT", "GET_UP_FRONT", "GET_UP_BACK"].includes(normalized ?? "") ? normalized as CharacterClipName : undefined;
}

function parseTrackPath(path: string | undefined): CharacterTrackPath | undefined {
  return path === "translation" || path === "rotation" || path === "scale" ? path : undefined;
}

function hasNonIdentityTransform(node: GltfNode): boolean {
  const translation = node.translation ?? [0, 0, 0];
  const rotation = node.rotation ?? [0, 0, 0, 1];
  const scale = node.scale ?? [1, 1, 1];
  return (
    translation.some((value) => Math.abs(value) > 0.000001) ||
    rotation.some((value, index) => Math.abs(value - (index === 3 ? 1 : 0)) > 0.000001) ||
    scale.some((value) => Math.abs(value - 1) > 0.000001)
  );
}

function vec3(values: readonly number[] | undefined, fallback: readonly [number, number, number]): [number, number, number] {
  return [finite(values?.[0], fallback[0]), finite(values?.[1], fallback[1]), finite(values?.[2], fallback[2])];
}

function quat(values: readonly number[] | undefined): [number, number, number, number] {
  const result: [number, number, number, number] = [
    finite(values?.[0], 0),
    finite(values?.[1], 0),
    finite(values?.[2], 0),
    finite(values?.[3], 1),
  ];
  const length = Math.hypot(...result) || 1;
  result[0] /= length;
  result[1] /= length;
  result[2] /= length;
  result[3] /= length;
  return result;
}

function finite(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function unit(value: number | undefined, fallback: number): number {
  return Math.max(0, Math.min(1, finite(value, fallback)));
}
